import { motion } from "framer-motion";
import { MessageSquare, Phone, Send, CalendarDays, CheckCircle2 } from "lucide-react";
import { BRAND, HERO_BADGES, IMAGES } from "@/data/site";
import { smsHref, telHref, scrollTo } from "@/lib/actions";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.6, ease: "easeOut" } }),
};

export const Hero = () => {
  return (
    <section id="home" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden bg-[#0A0A0A]">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${IMAGES.heroBg})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-black/50" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/60" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 pb-16 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.div initial="hidden" animate="show" custom={0} variants={fade} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#C8102E]/40 bg-[#C8102E]/10 mb-6">
              <span className="h-2 w-2 rounded-full bg-[#C8102E] animate-pulse" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/90">Raleigh Automotive Liaison</span>
            </motion.div>

            <motion.h1 initial="hidden" animate="show" custom={1} variants={fade} className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter uppercase leading-[0.92] text-white">
              Need a car? Talk to <span className="text-[#C8102E]">Malcolm</span> first.
            </motion.h1>

            <motion.p initial="hidden" animate="show" custom={2} variants={fade} className="mt-6 text-base lg:text-lg text-gray-300 max-w-xl leading-relaxed">
              Whether you have good credit, bad credit, need help rebuilding, need a personal vehicle, rideshare vehicle, or business vehicle guidance, Malcolm helps Raleigh-area drivers understand their options and take the next step with confidence.
            </motion.p>

            <motion.div initial="hidden" animate="show" custom={3} variants={fade} className="mt-8 flex flex-wrap gap-2.5">
              {HERO_BADGES.map((b) => (
                <span key={b} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-white/5 border border-white/10 text-xs font-medium text-gray-200">
                  <CheckCircle2 size={14} className="text-[#C8102E]" /> {b}
                </span>
              ))}
            </motion.div>

            <motion.div initial="hidden" animate="show" custom={4} variants={fade} className="mt-9 flex flex-wrap gap-3">
              <a href={smsHref} data-testid="hero-text-btn" className="inline-flex items-center gap-2 px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white bg-[#C8102E] rounded-sm hover:bg-[#A00D25] transition-all hover:-translate-y-0.5 shadow-lg shadow-[#C8102E]/30">
                <MessageSquare size={18} /> Text Malcolm
              </a>
              <a href={telHref} data-testid="hero-call-btn" className="inline-flex items-center gap-2 px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white border border-white/25 rounded-sm hover:bg-white/10 transition-all hover:-translate-y-0.5">
                <Phone size={18} /> Call Malcolm
              </a>
              <button onClick={() => scrollTo("#contact")} data-testid="hero-request-btn" className="inline-flex items-center gap-2 px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white border border-white/25 rounded-sm hover:bg-white/10 transition-all hover:-translate-y-0.5">
                <Send size={18} /> Send a Request
              </button>
              <button onClick={() => scrollTo("#contact")} data-testid="hero-schedule-btn" className="inline-flex items-center gap-2 px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white border border-white/25 rounded-sm hover:bg-white/10 transition-all hover:-translate-y-0.5">
                <CalendarDays size={18} /> Schedule Appointment
              </button>
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.3 }} className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-sm">
              <div className="absolute -inset-4 bg-[#C8102E]/20 blur-3xl rounded-full" />
              <div className="relative rounded-sm overflow-hidden border border-white/10 bg-gradient-to-b from-white/5 to-transparent">
                <img src={BRAND.headshot} alt="Malcolm Burke — Raleigh Automotive Liaison" className="w-full object-cover" />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/70 to-transparent p-5">
                  <p className="font-heading text-xl font-bold uppercase tracking-tight text-white">Malcolm Burke</p>
                  <p className="text-xs text-gray-300">Automotive Liaison · Raleigh, NC</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
